import React, { useState, useEffect } from 'react';
import AdminDashboardSkeleton from "../../components/layout/AdminDashboardSkeleton";
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useTheme } from '../../hooks/useTheme';
import api from '../../api/api';
import { Users, Settings, TrendingUp, UserCheck, Calendar, CheckCircle, XCircle, Loader, CreditCard } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';


export default function AdminDashboard() {
    const { user } = useAuth();
    const { isDarkMode } = useTheme();
    const navigate = useNavigate();

    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);

    const [cardTimeframe, setCardTimeframe] = useState('day');

    const [memberStats, setMemberStats] = useState({ count: 0, new: 0, percent: 0 });
    const [userStats, setUserStats] = useState({ count: 0, new: 0, percent: 0 });
    const [taskStats, setTaskStats] = useState({ count: 0, new: 0, percent: 0 });
    const [activeUsers, setActiveUsers] = useState({ current: 0, recent: 0 });

    const [growthHistory, setGrowthHistory] = useState([]);
    const [growthLoading, setGrowthLoading] = useState(true);
    const [timeframe, setTimeframe] = useState('day');

    const [memberGrowthHistory, setMemberGrowthHistory] = useState([]);
    const [memberGrowthLoading, setMemberGrowthLoading] = useState(true);
    const [memberTimeframe, setMemberTimeframe] = useState('day');
    const [memberTierNames, setMemberTierNames] = useState([]);
    const [activeTier, setActiveTier] = useState(null);

    useEffect(() => {
        if (!user || !user.isAdmin) {
            navigate('/userdashboard');
        }
    }, [user, navigate]);

    useEffect(() => {
        const fetchTotalUsers = async () => {
            setLoading(true);
            try {
                const res = await api.get('/admin/users', { params: { active_only: false, page: 1, per_page: 1 } });
                setUserStats(prev => ({ ...prev, count: res.data.total || 0 }));
            } catch (err) {
            } finally {
                setLoading(false);
            }
        };
        fetchTotalUsers();
    }, []);

    useEffect(() => {
        const fetchUserStats = async () => {
            try {
                const res = await api.get('/admin/user-growth-history', { params: { timeframe: cardTimeframe } });
                const history = res.data.history || [];
                const count = history.length > 0 ? history[history.length - 1].user_count : 0;
                const prev = history.length > 1 ? history[history.length - 2].user_count : 0;
                const newCount = count - prev;
                const percent = prev !== 0 ? ((newCount / prev) * 100).toFixed(1) : (count > 0 ? 100 : 0);
                setUserStats(prev => ({ ...prev, new: newCount, percent }));
            } catch (err) {
                setUserStats(prev => ({ ...prev, new: 0, percent: 0 }));
            }
        };
        fetchUserStats();
    }, [cardTimeframe]);

    useEffect(() => {
        // Fetch task stats for card (total + growth)
        const fetchTaskStats = async () => {
            try {
                const res = await api.get('/admin/task-growth-history', { params: { timeframe: cardTimeframe } });
                const history = res.data.history || [];
                const total = res.data.total || 0;
                const count = history.length > 0 ? history[history.length - 1].task_count : 0;
                const prev = history.length > 1 ? history[history.length - 2].task_count : 0;
                const newCount = count - prev;
                const percent = prev > 0 ? ((newCount / prev) * 100).toFixed(1) : (count > 0 ? 100 : 0);
                setTaskStats({
                    count: total,
                    new: newCount > 0 ? newCount : 0,
                    percent
                });
            } catch (err) {
                setTaskStats(prev => ({ ...prev, new: 0, percent: 0 }));
            }
        };
        fetchTaskStats();
    }, [cardTimeframe]);

    useEffect(() => {
        const fetchActiveUsers = async () => {
            try {
                const res = await api.get('/admin/active-users', { params: { timeframe: cardTimeframe } });
                setActiveUsers(res.data || { current: 0, recent: 0 });
            } catch (err) {
                setActiveUsers({ current: 0, recent: 0 });
            }
        };
        fetchActiveUsers();
    }, [cardTimeframe]);

    useEffect(() => {
        const fetchGrowthHistory = async () => {
            setGrowthLoading(true);
            try {
                const response = await api.get('/admin/user-growth-history', { params: { timeframe } });
                setGrowthHistory(response.data.history || []);
            } catch (err) {
                console.error('Error fetching user growth history:', err);
                setGrowthHistory([]);
            } finally {
                setGrowthLoading(false);
            }
        };
        fetchGrowthHistory();
    }, [timeframe]);

    useEffect(() => {
        const fetchMemberGrowthHistory = async () => {
            setMemberGrowthLoading(true);
            try {
                const response = await api.get('/admin/member-growth-history', { params: { timeframe: memberTimeframe } });
                setMemberGrowthHistory(response.data.history || []);
                setMemberTierNames(response.data.tiers || []);
            } catch (err) {
                console.error('Error fetching member growth history:', err);
                setMemberGrowthHistory([]);
                setMemberTierNames([]);
            } finally {
                setMemberGrowthLoading(false);
            }
        };
        fetchMemberGrowthHistory();
    }, [memberTimeframe]);

    if (loading) {
        return <AdminDashboardSkeleton isDarkMode={isDarkMode} />;
    }

    const getTimeframeLabel = (tf) => {
        switch (tf) {
            case 'day': return 'today';
            case 'week': return 'this week';
            case 'month': return 'this month';
            default: return '';
        }
    };

    const formatDateTime = (dt) => {
        if (!dt) return '-';
        const d = new Date(dt);
        if (isNaN(d)) return dt;
        return d.toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
    };

    const card = `rounded-2xl border shadow-sm p-6 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`;
    const selectCls = `px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-100' : 'border-slate-300 text-black'}`;

    return (
        <>
        <div className={`min-h-screen ${isDarkMode ? 'bg-surface' : 'bg-surface'}`}>
            <div className="max-w-7xl mx-auto px-4 py-8">
                {/* Header */}
                <header className="mb-8">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className={`text-3xl font-extrabold tracking-tight mb-2 ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Admin Dashboard</h1>
                            <p className={isDarkMode ? 'text-slate-400' : 'text-slate-600'}>Manage users, lookups, and view analytics</p>
                        </div>
                        <button
                            onClick={() => navigate('/admin/system-logs')}
                            className={`px-4 py-2 rounded-lg font-semibold shadow hover:bg-red-200 transition-colors flex items-center gap-2 ${isDarkMode ? 'bg-red-900/30 text-red-300 hover:bg-red-900/50' : 'bg-red-100 text-red-700'}`}
                            aria-label="View System Logs"
                        >
                            <XCircle size={20} />
                            System Logs
                        </button>
                    </div>
                </header>

                {/* Quick Stats */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 gap-4">
                    <h2 className={`text-lg font-semibold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Quick Stats</h2>
                    <div>
                        <select
                            value={cardTimeframe}
                            onChange={e => setCardTimeframe(e.target.value)}
                            className={`select-styled text-sm font-medium rounded-xl px-3 py-2 border shadow-sm ${isDarkMode ? 'bg-slate-800 border-slate-600 text-slate-200 hover:border-slate-500' : 'border-slate-300 bg-white text-slate-700 hover:border-slate-400'}`}
                        >
                            <option value="day">Daily</option>
                            <option value="week">Weekly</option>
                            <option value="month">Monthly</option>
                        </select>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    {/* Total Members Card (now shows total users and new users) */}
                    <div className={`rounded-2xl border shadow-sm p-6 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
                        <div className="flex items-center justify-between mb-4">
                            <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-primary-900/30 text-primary-400' : 'bg-primary-50 text-primary-600'}`}>
                                <UserCheck size={24} />
                            </div>
                        </div>
                        <h3 className={`text-sm font-semibold mb-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Total Members</h3>
                        <div className={`text-3xl font-bold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>{userStats.count}</div>
                        <div className="flex items-center gap-2 mt-2">
                            <span className="text-xs text-primary-600 font-semibold">+{userStats.new} new {getTimeframeLabel(cardTimeframe)}</span>
                            <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>({userStats.percent}% new)</span>
                        </div>
                    </div>
                    {/* Total Tasks Card */}
                    <div className={`rounded-2xl border shadow-sm p-6 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
                        <div className="flex items-center justify-between mb-4">
                            <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-amber-900/30 text-amber-400' : 'bg-amber-50 text-amber-600'}`}>
                                <Calendar size={24} />
                            </div>
                        </div>
                        <h3 className={`text-sm font-semibold mb-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Total Tasks</h3>
                        <div className={`text-3xl font-bold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>{taskStats.count}</div>
                        <div className="flex items-center gap-2 mt-2">
                            <span className="text-xs text-amber-600 font-semibold">+{taskStats.new} new {getTimeframeLabel(cardTimeframe)}</span>
                            <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>({taskStats.percent}% new)</span>
                        </div>
                    </div>
                    {/* Active Users Card */}
                    <div className={`rounded-2xl border shadow-sm p-6 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
                        <div className="flex items-center justify-between mb-4">
                            <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-50 text-blue-600'}`}>
                                <Users size={24} />
                            </div>
                        </div>
                        <h3 className={`text-sm font-semibold mb-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Active Users</h3>
                        <div className={`text-3xl font-bold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>{activeUsers.current}</div>
                        <div className="text-xs mt-2 text-slate-500">Recent (last 48h): {activeUsers.recent}</div>
                    </div>
                </div>

                {/* Growth Charts */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    {/* User Growth Chart Section */}
                    <div className={`rounded-2xl border shadow-sm p-6 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
                        <div className="flex items-center gap-4 mb-4">
                            <TrendingUp size={24} className="text-primary-600" />
                            <h2 className={`text-lg font-semibold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>User Growth Rate</h2>
                            <select
                                value={timeframe}
                                onChange={e => setTimeframe(e.target.value)}
                                className={`select-styled ml-auto text-sm font-medium rounded-xl px-3 py-2 border shadow-sm ${isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-200 hover:border-slate-500' : 'border-slate-300 bg-white text-slate-700 hover:border-slate-400'}`}
                            >
                                <option value="day">Daily</option>
                                <option value="week">Weekly</option>
                                <option value="month">Monthly</option>
                                <option value="year">Yearly</option>
                            </select>
                        </div>
                        {growthLoading ? (
                            <div className="flex items-center justify-center h-64">
                                <Loader className="animate-spin text-primary-600" size={32} />
                            </div>
                        ) : (
                            <ResponsiveContainer width="100%" height={300}>
                                <LineChart data={growthHistory} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#334155' : '#e2e8f0'} />
                                    <XAxis dataKey={timeframe === 'day' ? 'date' : timeframe} tick={{ fontSize: 12, fill: isDarkMode ? '#94a3b8' : '#64748b' }} tickFormatter={formatDateTime} />
                                    <YAxis tick={{ fontSize: 12, fill: isDarkMode ? '#94a3b8' : '#64748b' }} />
                                    <Tooltip labelFormatter={formatDateTime} contentStyle={isDarkMode ? { backgroundColor: '#1e293b', border: '1px solid #334155', color: '#f1f5f9' } : {}} />
                                    <Legend />
                                    <Line type="monotone" dataKey="user_count" stroke="#5A7CDE" name="Users" />
                                </LineChart>
                            </ResponsiveContainer>
                        )}
                    </div>

                    {/* Paid Membership Growth Chart Section */}
                    <div className={`rounded-2xl border shadow-sm p-6 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
                        <div className="flex items-center gap-4 mb-4">
                            <TrendingUp size={24} className="text-purple-600" />
                            <h2 className={`text-lg font-semibold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Paid Membership Growth Rate</h2>
                            <select
                                value={memberTimeframe}
                                onChange={e => setMemberTimeframe(e.target.value)}
                                className={`select-styled ml-auto text-sm font-medium rounded-xl px-3 py-2 border shadow-sm ${isDarkMode ? 'bg-slate-700 border-slate-600 text-slate-200 hover:border-slate-500' : 'border-slate-300 bg-white text-slate-700 hover:border-slate-400'}`}
                            >
                                <option value="day">Daily</option>
                                <option value="week">Weekly</option>
                                <option value="month">Monthly</option>
                                <option value="year">Yearly</option>
                            </select>
                        </div>
                        {memberGrowthLoading ? (
                            <div className="flex items-center justify-center h-64">
                                <Loader className="animate-spin text-purple-600" size={32} />
                            </div>
                        ) : (
                            <ResponsiveContainer width="100%" height={300}>
                                <LineChart data={memberGrowthHistory} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis
                                        dataKey={memberTimeframe === 'day' ? 'date' : memberTimeframe}
                                        tick={{ fontSize: 12 }}
                                        tickFormatter={formatDateTime}
                                    />
                                    <YAxis tick={{ fontSize: 12 }} />
                                    <Tooltip labelFormatter={formatDateTime} />
                                    <Legend
                                        onClick={(e) => setActiveTier(prev => prev === e.dataKey ? null : e.dataKey)}
                                        wrapperStyle={{ cursor: 'pointer' }}
                                    />
                                    {memberTierNames.map((tier, idx) => {
                                        const colors = ['#a855f7', '#ec4899', '#f59e0b', '#06b6d4', '#84cc16', '#ef4444'];
                                        const isHidden = activeTier && activeTier !== tier;
                                        return (
                                            <Line
                                                key={tier}
                                                type="monotone"
                                                dataKey={tier}
                                                stroke={colors[idx % colors.length]}
                                                name={tier}
                                                hide={isHidden}
                                                strokeOpacity={isHidden ? 0 : 1}
                                            />
                                        );
                                    })}
                                </LineChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </div>

                {/* Quick Actions */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                    <button
                        onClick={() => navigate('/admin/users')}
                        className={`rounded-2xl border shadow-sm p-6 hover:shadow-md transition-shadow text-left ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}
                    >
                        <div className="flex items-center gap-4">
                            <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-50 text-blue-600'}`}>
                                <Users size={24} />
                            </div>
                            <div>
                                <h3 className={`font-semibold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Manage Users</h3>
                                <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>View and manage user accounts</p>
                            </div>
                        </div>
                    </button>
                    <button
                        onClick={() => navigate('/admin/employees')}
                        className={`rounded-2xl border shadow-sm p-6 hover:shadow-md transition-shadow text-left ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}
                    >
                        <div className="flex items-center gap-4">
                            <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-yellow-900/30 text-yellow-400' : 'bg-yellow-50 text-yellow-600'}`}>
                                <UserCheck size={24} />
                            </div>
                            <div>
                                <h3 className={`font-semibold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Employee Management</h3>
                                <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Manage employees and roles</p>
                            </div>
                        </div>
                    </button>
                    <button
                        onClick={() => navigate('/admin/lookups')}
                        className={`rounded-2xl border shadow-sm p-6 hover:shadow-md transition-shadow text-left ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}
                    >
                        <div className="flex items-center gap-4">
                            <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-primary-900/30 text-primary-400' : 'bg-primary-50 text-primary-600'}`}>
                                <Settings size={24} />
                            </div>
                            <div>
                                <h3 className={`font-semibold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Manage Lookups</h3>
                                <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Edit personas, intents, industries, positions</p>
                            </div>
                        </div>
                    </button>
                    <button
                        onClick={() => navigate('/admin/memberships')}
                        className={`rounded-2xl border shadow-sm p-6 hover:shadow-md transition-shadow text-left ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}
                    >
                        <div className="flex items-center gap-4">
                            <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-purple-900/30 text-purple-400' : 'bg-purple-50 text-purple-600'}`}>
                                <CreditCard size={24} />
                            </div>
                            <div>
                                <h3 className={`font-semibold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Membership Management</h3>
                                <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Manage tiers and view active members</p>
                            </div>
                        </div>
                    </button>
                </div>
            </div>
        </div>
        </>
    );
}
